import {
  metricKeys,
  type ExpExactEvidence,
  type GameStateSnapshot,
  type MetricKey,
  type MetricReading,
  type RawReading,
  type ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import {
  enrichExpRequirement,
  EXP_RATIO_MATCH_TOLERANCE,
  expMatchesRequirement,
  ExpRequirementEstimator,
} from './exp-requirement-estimator'

type PreviousReadings = { -readonly [Key in MetricKey]?: RawReading[Key] }

const EXP_REBASELINE_WINDOW_MS = 10_000

interface PendingExpRebaseline {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  readonly inferredRequired: number
  readonly capturedAt: number
  readonly confirmations: number
}

interface TrustedExpEvidencePoint {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  readonly requiredExp: number | null
  readonly sourceAt: number
}

interface ExactEvidenceDecision {
  readonly accepted: boolean
  readonly evidence?: ExpExactEvidence
}

export class ReadingValidator {
  private previous: PreviousReadings = {}
  private pendingLevelIncrease: { from: number; to: number; capturedAt: number } | null = null
  private pendingExactExpRebaseline: PendingExpRebaseline | null = null
  private trustedExactExp: TrustedExpEvidencePoint | null = null
  private readonly expRequirements = new ExpRequirementEstimator()
  private mesosNeedsBaseline = false
  private mesosContinuityId: number | null = null
  private confirmedExpRebaseline = false
  private confirmedExactExpRebaseline = false

  constructor(
    private readonly preset: GamePreset,
    private minimumConfidence = 0.62,
  ) {}

  setMinimumConfidence(value: number): void {
    this.minimumConfidence = value
  }

  resetMetricContinuity(metrics: readonly MetricKey[]): void {
    for (const metric of metrics) delete this.previous[metric]
    if (metrics.includes('mesos')) {
      this.mesosNeedsBaseline = true
      this.mesosContinuityId = null
    }
  }

  validate(reading: RawReading): ValidatedObservation {
    this.confirmedExpRebaseline = false
    this.confirmedExactExpRebaseline = false
    if (this.pendingLevelIncrease && reading.capturedAt - this.pendingLevelIncrease.capturedAt > 15_000) {
      this.pendingLevelIncrease = null
    }
    if (reading.mesosContinuityId !== undefined && reading.mesosContinuityId !== this.mesosContinuityId) {
      this.mesosContinuityId = reading.mesosContinuityId
      this.mesosNeedsBaseline = true
      delete this.previous.mesos
    }
    if (reading.mesos.availability === 'unavailable') this.mesosNeedsBaseline = true
    const previousLevel = this.previous.level?.value
    const previousExpRatio = this.previous.exp?.value?.ratio
    const exactDecision = this.acceptExpExactEvidence(reading.expExactEvidence)
    const preparedReading = this.withObservedExpRequirement(reading, exactDecision.evidence)
    const accepted = Object.fromEntries(metricKeys.map((key) => [key, this.acceptMetric(key, preparedReading)])) as Record<MetricKey, boolean>
    const observation: ValidatedObservation = {
      ...preparedReading,
      accepted,
      ...(reading.expExactEvidence ? { expExactEvidenceAccepted: exactDecision.accepted } : {}),
      ...(this.confirmedExpRebaseline ? { expRebaseline: true as const } : {}),
      ...(this.confirmedExactExpRebaseline ? { expExactRebaseline: true as const } : {}),
    }
    if (accepted.level && typeof previousLevel === 'number' && preparedReading.level.value === previousLevel + 1) {
      this.pendingLevelIncrease = { from: previousLevel, to: preparedReading.level.value, capturedAt: preparedReading.capturedAt }
    }
    if (accepted.exp && typeof previousExpRatio === 'number' && preparedReading.exp.value?.ratio !== null && preparedReading.exp.value!.ratio! < previousExpRatio - 0.08) {
      this.pendingLevelIncrease = null
    }
    if (observation.expRebaseline) this.pendingLevelIncrease = null
    if (accepted.level) this.previous.level = preparedReading.level
    if (accepted.exp) this.previous.exp = preparedReading.exp
    if (accepted.hp) this.previous.hp = preparedReading.hp
    if (accepted.mp) this.previous.mp = preparedReading.mp
    if (accepted.mesos) {
      this.previous.mesos = preparedReading.mesos
      this.mesosNeedsBaseline = false
    }
    if (accepted.mapName) this.previous.mapName = preparedReading.mapName
    return observation
  }

  resetBaseline(state: GameStateSnapshot | null = null): void {
    this.previous = state ? {
      ...(state.level === null ? {} : { level: this.seedReading('level', state.level, state) }),
      ...(state.exp.absolute === null && state.exp.ratio === null ? {} : { exp: this.seedReading('exp', state.exp, state) }),
      ...(state.hp.current === null && state.hp.ratio === null ? {} : { hp: this.seedReading('hp', state.hp, state) }),
      ...(state.mp.current === null && state.mp.ratio === null ? {} : { mp: this.seedReading('mp', state.mp, state) }),
      ...(state.mesos === null ? {} : { mesos: this.seedReading('mesos', state.mesos, state) }),
      ...(state.mapName === null ? {} : { mapName: this.seedReading('mapName', state.mapName, state) }),
    } : {}
    this.pendingLevelIncrease = null
    this.pendingExactExpRebaseline = null
    this.trustedExactExp = state ? this.exactEvidencePoint(state) : null
    this.expRequirements.reset(state?.level === null || state?.level === undefined
      ? null
      : { level: state.level, value: state.exp })
    this.mesosNeedsBaseline = false
    this.mesosContinuityId = null
  }

  private acceptExpExactEvidence(evidence: ExpExactEvidence | undefined): ExactEvidenceDecision {
    if (!evidence) return { accepted: false }
    const value = evidence.value
    const level = evidence.level
    if (evidence.confirmation !== 'source-frame' && evidence.confirmation !== 'consensus') return { accepted: false }
    if (evidence.confidence < this.minimumConfidence
      || level === null
      || !Number.isInteger(level)
      || level < 1
      || level > 200
      || value.estimated
      || value.absolute === null
      || value.ratio === null
      || !Number.isSafeInteger(value.absolute)
      || value.absolute < 0
      || value.ratio < 0
      || value.ratio > 1
      || evidence.sourceAt > evidence.deliveredAt) {
      return { accepted: false }
    }

    const previous = this.trustedExactExp
    if (previous && level > previous.level && value.absolute === 0 && value.ratio === 0) {
      this.expRequirements.seed(level, value)
    }
    const requirement = this.expRequirements.observe(level, value, evidence.sourceAt, {
      percentagePrecision: evidence.percentagePrecision,
      independentlyConfirmed: true,
    })
    if (!requirement.accepted) return { accepted: false }
    const enrichedValue = enrichExpRequirement(value, requirement.requiredExp, requirement.approximate)
    const enrichedEvidence: ExpExactEvidence = { ...evidence, value: enrichedValue }

    const point: TrustedExpEvidencePoint = {
      level,
      absolute: value.absolute,
      ratio: value.ratio,
      requiredExp: requirement.requiredExp,
      sourceAt: evidence.sourceAt,
    }
    if (!previous) {
      this.trustedExactExp = point
      this.pendingExactExpRebaseline = null
      return { accepted: true, evidence: enrichedEvidence }
    }
    if (point.sourceAt <= previous.sourceAt) return { accepted: false }
    if (point.level !== previous.level) {
      const sequentialWrap = point.level === previous.level + 1
        && (point.absolute < previous.absolute || point.ratio < previous.ratio - EXP_RATIO_MATCH_TOLERANCE)
      this.trustedExactExp = point
      this.pendingExactExpRebaseline = null
      if (!sequentialWrap) this.confirmedExactExpRebaseline = true
      return { accepted: true, evidence: enrichedEvidence }
    }
    if (point.absolute >= previous.absolute) {
      this.trustedExactExp = point
      this.pendingExactExpRebaseline = null
      return { accepted: true, evidence: enrichedEvidence }
    }

    const pending = this.pendingExactExpRebaseline
    const continuesSequence = pending !== null
      && pending.level === level
      && point.sourceAt > pending.capturedAt
      && point.sourceAt - pending.capturedAt <= EXP_REBASELINE_WINDOW_MS
      && point.absolute >= pending.absolute
      && point.ratio >= pending.ratio - EXP_RATIO_MATCH_TOLERANCE
      && requirementsAreCompatible(requirement.requiredExp, pending.inferredRequired)
    if (continuesSequence) {
      this.pendingExactExpRebaseline = null
      this.trustedExactExp = point
      this.confirmedExactExpRebaseline = true
      return { accepted: true, evidence: enrichedEvidence }
    }

    this.pendingExactExpRebaseline = {
      level,
      absolute: point.absolute,
      ratio: point.ratio,
      inferredRequired: requirement.requiredExp ?? 0,
      capturedAt: point.sourceAt,
      confirmations: 1,
    }
    return { accepted: false }
  }

  private exactEvidencePoint(state: GameStateSnapshot): TrustedExpEvidencePoint | null {
    if (state.level === null || state.exp.absolute === null || state.exp.ratio === null || state.exp.estimated) return null
    const required = state.exp.requiredExp ?? null
    if (state.exp.absolute < 0
      || state.exp.ratio < 0
      || state.exp.ratio > 1
      || !expMatchesRequirement(state.exp, required)) {
      return null
    }
    return {
      level: state.level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      requiredExp: required,
      sourceAt: state.observedAt?.exp ?? state.capturedAt,
    }
  }

  private withObservedExpRequirement(reading: RawReading, evidence?: ExpExactEvidence): RawReading {
    const level = evidence?.level ?? (typeof reading.level.value === 'number' ? reading.level.value : null)
    const value = reading.exp.value
    if (level === null || value === null) {
      return evidence ? { ...reading, expExactEvidence: evidence } : reading
    }
    const observed = evidence?.value.requiredExp
    const known = observed !== undefined ? observed : this.expRequirements.requirementFor(level)
    if (known === undefined) return evidence ? { ...reading, expExactEvidence: evidence } : reading
    const exp = {
      ...reading.exp,
      value: enrichExpRequirement(value, known, known !== null),
    }
    return { ...reading, exp, ...(evidence ? { expExactEvidence: evidence } : {}) }
  }

  private seedReading<Key extends MetricKey>(metric: Key, value: NonNullable<RawReading[Key]['value']>, state: GameStateSnapshot): MetricReading<typeof value> {
    return {
      value,
      confidence: state.confidence[metric],
      source: this.preset.capabilities[metric] === 'ocr' ? 'ocr' : 'fused',
      availability: 'available',
      capturedAt: state.observedAt?.[metric] ?? state.capturedAt,
    }
  }

  private acceptMetric(metric: MetricKey, reading: RawReading): boolean {
    if (this.preset.capabilities[metric] === 'unavailable') return false

    if (metric === 'level') {
      const candidate = reading.level
      const previous = this.previous.level
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isInteger(value) || value < 1 || value > 200) return false
      if (previous?.value === null || previous?.value === undefined) return candidate.confirmation === 'consensus'
      if (value === previous.value) return true
      return candidate.confirmation === 'consensus'
    }

    if (metric === 'mesos') {
      const candidate = reading.mesos
      const previous = this.previous.mesos
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isSafeInteger(value) || value < 0) return false
      if (this.mesosNeedsBaseline) return true
      if (previous?.value === null || previous?.value === undefined) return true
      return true
    }

    if (metric === 'mapName') {
      const candidate = reading.mapName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value.trim()
      return value.length >= 2 && value.length <= 32 && /[\u3400-\u9fffA-Za-z0-9]/.test(value)
    }

    if (metric === 'exp') {
      const candidate = reading.exp
      const previous = this.previous.exp
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value
      if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
      if (value.absolute !== null && value.absolute < 0) return false
      const level = reading.level.value
      const simultaneousLevelUp = typeof level === 'number'
        && typeof this.previous.level?.value === 'number'
        && level === this.previous.level.value + 1
      const pendingLevelUp = this.pendingLevelIncrease !== null
        && level === this.pendingLevelIncrease.to
        && reading.capturedAt - this.pendingLevelIncrease.capturedAt <= 15_000
      const levelUpSupported = simultaneousLevelUp || pendingLevelUp
      if (value.absolute !== null && value.ratio !== null) {
        const zeroEndpoint = value.absolute === 0 && value.ratio === 0
        const exactIsConfirmed = zeroEndpoint
          ? levelUpSupported || value.requiredExp !== undefined
          : value.requiredExp !== undefined && expMatchesRequirement(value, value.requiredExp ?? null)
        if (!exactIsConfirmed) return false
      }
      const previousRatio = previous?.value?.ratio
      const previousAbsolute = previous?.value?.absolute
      const ratioReset = typeof previousRatio === 'number'
        && value.ratio !== null
        && (value.ratio < previousRatio - 0.018 || (value.ratio <= 0.001 && previousRatio > 0.001))
      const absoluteReset = typeof previousAbsolute === 'number'
        && value.absolute !== null
        && value.absolute < previousAbsolute
      if (ratioReset || absoluteReset) {
        if (!levelUpSupported && !this.confirmedExactExpRebaseline) return false
        if (!levelUpSupported) this.confirmedExpRebaseline = true
      }
      if (candidate.source === 'ocr' && reading.diagnostics.metrics?.exp?.status === 'confirming') return false
      return true
    }

    const candidate = metric === 'hp' ? reading.hp : reading.mp
    if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
    const value = candidate.value
    if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
    if (value.current !== null && value.current < 0) return false
    if (value.max !== null && (value.max <= 0 || (value.current !== null && value.current > value.max * 1.05))) return false
    return true
  }

}

function requirementsAreCompatible(current: number | null, previous: number): boolean {
  if (current === null || previous <= 0) return current === null && previous === 0
  return Math.abs(current - previous) / previous <= 0.005
}
